/**
 * `/vibe <prompt>` chat command — sends the prompt to agent-service so the
 * room's game-module gets edited + hot-swapped without the user needing to
 * leave the browser. Result shows as a transient toast and is also broadcast
 * to room chat so other players see what was asked.
 *
 * Auth: caller passes a token (typically `getStoredToken()` from auth.ts).
 * We add `Authorization: Bearer <token>` if present.
 */

import type { ChatMessageLike } from "./types";
import { getStoredToken } from "./auth";

const VIBE_KEYWORDS = ["vibe", "build", "spawn", "make"];
const VIBE_REGEX = new RegExp(`^/(${VIBE_KEYWORDS.join("|")})(?:\\s+(.*))?$`, "i");

export interface ParsedVibe {
  /** True if the raw text was a slash-command we handle. */
  isVibe: boolean;
  /** Prompt body after the prefix. Undefined when isVibe is false or body is empty. */
  prompt?: string;
}

export function parseVibeCommand(raw: string): ParsedVibe {
  const m = raw.trim().match(VIBE_REGEX);
  if (!m) return { isVibe: false };
  const body = m[2]?.trim();
  return { isVibe: true, prompt: body && body.length > 0 ? body : undefined };
}

export interface VibeRequest {
  agentUrl: string;
  roomSlug: string;
  playerId: string;
  prompt: string;
  recentChat: ChatMessageLike[];
  stateSnapshot?: Record<string, unknown>;
  /** Bearer token (passed by caller; main.ts reads readStoredToken() before calling). */
  token?: string | null;
}

export type VibeResult =
  | { ok: true; sha: string; summary: string; filesChanged: string[] }
  | { ok: false; error: string };

/** Re-export so main.ts can stay on a single import line. */
export const readStoredToken = getStoredToken;

export async function sendVibePrompt(req: VibeRequest, fetchFn: typeof fetch = fetch): Promise<VibeResult> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (req.token) headers["authorization"] = `Bearer ${req.token}`;

  let res: Response;
  try {
    res = await fetchFn(`${req.agentUrl.replace(/\/$/, "")}/process-prompt`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        roomId: req.roomSlug,
        playerId: req.playerId,
        prompt: req.prompt,
        stateSnapshot: req.stateSnapshot ?? {},
        recentChat: req.recentChat.slice(-10),
      }),
    });
  } catch (err) {
    return { ok: false, error: networkError(err) };
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { ok: false, error: `agent-service returned ${res.status} with non-JSON body` };
  }

  if (!res.ok) {
    const msg = isObject(body) && typeof body["error"] === "string" ? body["error"] : `HTTP ${res.status}`;
    return { ok: false, error: msg };
  }

  if (
    isObject(body) &&
    body["status"] === "ok" &&
    typeof body["sha"] === "string" &&
    typeof body["summary"] === "string"
  ) {
    const files = Array.isArray(body["filesChanged"])
      ? body["filesChanged"].filter((f): f is string => typeof f === "string")
      : [];
    return { ok: true, sha: body["sha"], summary: body["summary"], filesChanged: files };
  }

  if (isObject(body) && body["status"] === "error" && typeof body["error"] === "string") {
    return { ok: false, error: body["error"] };
  }

  return { ok: false, error: `unexpected agent-service response shape` };
}

function networkError(err: unknown): string {
  if (err instanceof TypeError) return `network error: ${err.message}`;
  if (err instanceof Error) return err.message;
  return String(err);
}

function isObject(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

/**
 * Tiny non-blocking toast at the top of the viewport. Stack-aware: multiple
 * concurrent toasts stack vertically and auto-dismiss after `ttlMs`.
 *
 * We keep this in vibeCommand.ts (not a shared toast module) because it's
 * the only consumer for now. Pull it out if a second feature needs it.
 */
export type ToastKind = "info" | "ok" | "error";

export interface ToastHandle {
  /** Replace the toast's text + kind + optional new ttl. */
  update(text: string, kind?: ToastKind, ttlMsOverride?: number): void;
  /** Dismiss immediately. */
  dismiss(): void;
}

const TOAST_STACK_ID = "vibe-toasts";

export function showToast(text: string, kind: ToastKind = "info", ttlMs = 6000): ToastHandle {
  const stack = ensureStack();
  const el = document.createElement("div");
  el.className = `vibe-toast vibe-toast-${kind}`;
  el.textContent = text;
  applyToastStyle(el, kind);
  stack.appendChild(el);

  let timer = ttlMs > 0 ? window.setTimeout(dismiss, ttlMs) : 0;

  function dismiss(): void {
    if (timer) window.clearTimeout(timer);
    timer = 0;
    el.style.opacity = "0";
    setTimeout(() => el.remove(), 250);
  }

  return {
    update(nextText, nextKind, ttlMsOverride) {
      el.textContent = nextText;
      if (nextKind && nextKind !== kind) {
        kind = nextKind;
        el.className = `vibe-toast vibe-toast-${kind}`;
        applyToastStyle(el, kind);
      }
      if (timer) window.clearTimeout(timer);
      const effectiveTtl = ttlMsOverride ?? ttlMs;
      timer = effectiveTtl > 0 ? window.setTimeout(dismiss, effectiveTtl) : 0;
    },
    dismiss,
  };
}

function ensureStack(): HTMLDivElement {
  let stack = document.getElementById(TOAST_STACK_ID) as HTMLDivElement | null;
  if (stack) return stack;
  stack = document.createElement("div");
  stack.id = TOAST_STACK_ID;
  stack.style.cssText = [
    "position:fixed",
    "top:12px",
    "left:50%",
    "transform:translateX(-50%)",
    "z-index:9999",
    "display:flex",
    "flex-direction:column",
    "gap:6px",
    "pointer-events:none",
    "font-family:system-ui,sans-serif",
    "font-size:13px",
  ].join(";");
  document.body.appendChild(stack);
  return stack;
}

function applyToastStyle(el: HTMLDivElement, kind: ToastKind): void {
  const bg = kind === "ok" ? "rgba(34,170,68,0.92)" : kind === "error" ? "rgba(200,60,60,0.92)" : "rgba(40,40,50,0.92)";
  el.style.cssText = [
    `background:${bg}`,
    "color:#fff",
    "padding:8px 14px",
    "border-radius:6px",
    "box-shadow:0 4px 12px rgba(0,0,0,0.35)",
    "max-width:480px",
    "white-space:pre-wrap",
    "transition:opacity 250ms",
    "opacity:1",
  ].join(";");
}
